import { normalizePortKey } from '../../../lib/utils';

export const PAGE_SIZE = 5;
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;

export const isHuntNumberType = type =>
  type === 'hunt' || type === 'modemHunt' || type === 't38hunt';

const portTypeLabel = { modemHunt: 'Modem', t38hunt: 'Fax' };

export const getBoxToBoxHuntInvalidMessage = type =>
  `Selection no longer matches this hunt group. Remove it from Box-to-Box configuration, or set the same ports to ${portTypeLabel[type] || 'Voice'} on the child box.`;

export const boxSerialLabel = box => {
  const serial = String(box?.serialNumber || box?.label || '').trim();
  if (!serial || OBJECT_ID_RE.test(serial) || serial === String(box?._id)) return '';
  return serial;
};

export const resolveHuntPorts = (huntPorts, currentPort) => {
  const ports = Array.isArray(huntPorts) ? huntPorts : huntPorts ? [huntPorts] : [];
  const seen = new Set();
  return [...ports, currentPort]
    .filter(Boolean)
    .map(p => String(p).trim())
    .filter(p => {
      const key = normalizePortKey(p);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const boxId = ref => (ref && typeof ref === 'object' ? ref._id : ref) || null;

export const buildFormSync = (selected, eligible) => {
  const selections = [];
  (selected || []).forEach(box => {
    (box.matchingPorts || []).forEach(port => {
      selections.push({ box: box._id, port: String(port) });
    });
  });
  return {
    huntBoxes: [...new Set((selected || []).map(box => boxId(box)).filter(Boolean).map(String))],
    boxToBoxHuntSelections: selections,
    boxToBoxHuntInvalid:
      Array.isArray(eligible) &&
      (selected || []).some(s => !eligible.some(e => e.optionKey === s.optionKey)),
  };
};

export const restoreSelectedHuntBoxes = (selections, huntPorts, numberRecord) => {
  const serials = {};
  (numberRecord?.huntBoxes || []).forEach(box => {
    const id = boxId(box);
    const label = box?.serialNumber || box?.displayName;
    if (id && label && !OBJECT_ID_RE.test(String(label))) serials[String(id)] = String(label);
  });

  const grouped = {};
  (selections || []).forEach(sel => {
    const id = boxId(sel?.box);
    const port = sel?.port;
    if (!id || !port) return;
    const key = String(id);
    if (!grouped[key]) {
      grouped[key] = {
        _id: id,
        serialNumber: serials[key] || sel.box?.serialNumber || '',
        ports: [],
      };
    }
    grouped[key].ports.push(String(port));
  });

  return Object.values(grouped).map(box => ({
    _id: box._id,
    serialNumber: box.serialNumber,
    matchingPorts: box.ports.length ? box.ports : huntPorts.map(String),
    optionKey: String(box._id),
    label: boxSerialLabel(box),
  }));
};

export async function resolveSelectedBoxSerials(api, boxes) {
  const missing = (boxes || []).filter(box => !boxSerialLabel(box));
  if (!missing.length) return boxes;

  const serials = {};
  await Promise.all(
    missing.map(async box => {
      try {
        const data = await api.boxes.getBoxDetail({ _id: box._id });
        const serial = data?.serialNumber || data?.box?.serialNumber;
        if (serial && !OBJECT_ID_RE.test(String(serial))) serials[String(box._id)] = String(serial);
      } catch (_) {}
    }),
  );

  return (boxes || []).map(box => {
    const serial = boxSerialLabel(box) || serials[String(box._id)];
    return serial ? { ...box, serialNumber: serial, label: serial } : box;
  });
}
export async function validateSelectedHuntBoxes({
  api,
  parentBoxId,
  huntPorts,
  type,
  numberId,
  selected,
}) {
  if (!selected?.length || !parentBoxId || !huntPorts?.length) return false;

  const results = await Promise.all(
    selected.map(async box => {
      const serial = boxSerialLabel(box) || String(box.serialNumber || '').trim();
      if (!serial || OBJECT_ID_RE.test(serial)) return null;
      try {
        const data = await api.boxes.getEligibleBoxToBoxHunt(parentBoxId, {
          huntPorts,
          type,
          page: 1,
          limit: 5,
          search: serial,
          numberId,
        });
        return (data?.docs || []).some(d => d.optionKey === box.optionKey);
      } catch (_) {
        return null;
      }
    }),
  );

  const checked = results.filter(result => result !== null);
  if (!checked.length) return false;
  return checked.some(isEligible => !isEligible);
}

export const mergeEligiblePages = (existing, next) => {
  const keys = new Set((existing || []).map(row => row.optionKey));
  const merged = [...(existing || [])];
  (next || []).forEach(row => {
    if (!keys.has(row.optionKey)) {
      keys.add(row.optionKey);
      merged.push(row);
    }
  });
  return merged;
};

export const prepareBoxToBoxHuntSave = changedNumber => {
  if (!changedNumber || !isHuntNumberType(changedNumber.type)) return true;
  if (changedNumber.boxToBoxHuntInvalid) return false;

  if (Object.prototype.hasOwnProperty.call(changedNumber, 'boxToBoxHuntSelections')) {
    changedNumber.boxToBoxHuntSelections = (changedNumber.boxToBoxHuntSelections || [])
      .map(sel => {
        const id = boxId(sel?.box);
        return id && sel?.port ? { box: String(id), port: String(sel.port) } : null;
      })
      .filter(Boolean);
  }

  changedNumber.huntBoxes = [...new Set((changedNumber.huntBoxes || []).map(boxId).filter(Boolean).map(String))];
  delete changedNumber.boxToBoxHuntNumber;
  delete changedNumber.boxToBoxHuntInvalid;
  return true;
};
